import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QuizProvider } from "@/contexts/QuizContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { DomainRedirect } from "@/components/DomainRedirect";
import { useSPAPageTracking } from "@/hooks/useSPAPageTracking";
import { lazy, Suspense } from "react";

// Lazy load all routes for code splitting
const Index = lazy(() => import("./pages/Index"));
const HomeOld1 = lazy(() => import("./pages/HomeOld1"));
const Quiz = lazy(() => import("./pages/Quiz"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CareerPaths = lazy(() => import("./pages/CareerPaths"));
const Benefits = lazy(() => import("./pages/Benefits"));
const EmailTemplate = lazy(() => import("./pages/EmailTemplate"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Dashboard routes
const DashboardLayout = lazy(() => import("./components/recruiting/DashboardLayout").then(m => ({ default: m.DashboardLayout })));
const DashboardHome = lazy(() => import("./pages/dashboard/DashboardHome"));
const PipelineDashboard = lazy(() => import("./pages/dashboard/PipelineDashboard"));
const ProspectsDashboard = lazy(() => import("./pages/dashboard/ProspectsDashboard"));
const MessagesDashboard = lazy(() => import("./pages/dashboard/MessagesDashboard"));
const PlaceholderPage = lazy(() => import("./pages/dashboard/PlaceholderPage"));

// Loading component
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const PageTracker = () => {
  useSPAPageTracking();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <DomainRedirect />
      <BrowserRouter>
        <PageTracker />
        <AuthProvider>
          <QuizProvider>
            <Toaster />
            <Sonner />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/home/old1" element={<HomeOld1 />} />
                <Route path="/quiz" element={<Quiz />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin/gaarng" element={<Admin />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/career-paths" element={<CareerPaths />} />
                <Route path="/benefits" element={<Benefits />} />
                <Route path="/email-template" element={<EmailTemplate />} />
                
                {/* Recruiting Dashboard Routes */}
                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<DashboardHome />} />
                  <Route path="pipeline" element={<PipelineDashboard />} />
                  <Route path="prospects" element={<ProspectsDashboard />} />
                  <Route path="messages" element={<MessagesDashboard />} />
                  <Route path="recruiters" element={<PlaceholderPage />} />
                  <Route path="analytics" element={<PlaceholderPage />} />
                  <Route path="settings" element={<PlaceholderPage />} />
                </Route>
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </QuizProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
